import { TResourceConfiguration, View } from'../../../pip/library/pi-interop/pi-interop.js';
import navigation from'../../../pip/library/pi-navigation/pi-navigation.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';

import { IViewConfiguration } from '../types/configuration';
import { PointCut } from '../transformations/point_cut';

type TInvoke = HTMLElement & { invoke: () => void };

declare global {
  interface Window {
    ['HIP']: {
      browser: {
        queryOne(gid: string, ns: string): TInvoke;
      };
    };
  }
}

interface IAiView extends HTMLElement {
  ns: string;
}

interface IViewHILConfiguration extends IViewConfiguration, TResourceConfiguration {
  environment: 'local' | 'hosted' | 'dev' | 'qa' | 'prod' | string;
  constants: Record<string, string | number | boolean>;
}

interface IStandardHIL extends Record<string, unknown> {
  configuration?: Record<string, unknown>;
  parameters?: Record<string, unknown>;
  constants?: Record<string, unknown>;
  system?: Record<string, unknown>;
}

interface IAIViewElement extends HTMLElement {
  pointCutScript: (script: IStandardHIL) => IStandardHIL;
  autoRunScript: IStandardHIL;
}

/**
 * a view for rendering a single HIL file
 */
export class ViewHIL extends View {
  private __model: IStandardHIL;

  readonly lens = {
    environment: f1.lens('/environment'),
    parameters: f1.lens('/parameters'),
    constants: f1.lens('/constants')
  };

  configuration: IViewHILConfiguration;

  constructor(model: IStandardHIL) {
    super();
    this.__model = model;
  }

  mount(region: HTMLElement): Promise<this> {
    const viewEl = document.createElement('ai-view') as IAIViewElement;
    const hil: IStandardHIL = this.__model;

    viewEl.autoRunScript = f1.pipe(
      [this._writeSettingsToHIL.bind(this), this._writeConstants.bind(this), this._writeParameters.bind(this)],
      hil
    );

    // @todo add pointCut
    //
    const pointCuts = this.configuration.point_cuts;
    viewEl.pointCutScript = function (script) {
      return new PointCut().overlayPointCut(pointCuts, location.hash.slice(1), script);
    };

    region.append(viewEl);

    return new Promise((resolve) => {
      viewEl.addEventListener('invoke-ended', () => resolve(this), {
        once: true
      });
    });
  }

  protected _writeSettingsToHIL(hil: IStandardHIL) {
    // we should always be in production mode by default because if there
    // were an error in production we should fault to showing the production version
    //
    const environment = f1.read(this.lens.environment, 'prod', this.configuration);
    // this should not be used, but the transpiler wants the value for now
    // it affects old resource loading code in the transpiler
    // we will set it to `prod`
    const release = 'prod';

    const pickSettings = ['environment', 'release', 'title', 'state', 'mode'];
    const settings = {
      ...(navigation.location.active.parameters || {}),
      environment,
      release
    };

    f1.each(
      (k: string) => {
        f1.write(f1.lens(`/system/${k}`), settings[k], hil);
      },
      f1.filter((k) => f1.is.exists(settings[k]), pickSettings)
    );

    return hil;
  }

  protected _writeConstants(hil: IStandardHIL) {
    const lens = this.lens.constants;
    const existing = f1.read(lens, {}, hil);
    const configuration = f1.read(lens, {}, this.configuration);
    const constants = f1.read(lens, {}, this.configuration);

    f1.write(lens, f1.merge('over', f1.merge('over', existing, configuration), constants), hil);
    return hil;
  }

  protected _writeParameters(hil: IStandardHIL) {
    const lens = this.lens.parameters;
    const existing = f1.read(lens, {}, hil);
    const configuration = f1.read(lens, {}, this.configuration);
    const location = f1.read(lens, {}, navigation.location.active);

    f1.write(lens, f1.merge('over', f1.merge('over', existing, configuration), location), hil);
    return hil;
  }

  /**
   * uninitialize the view
   */
  uninitialize() {
    const viewEl = this.region.childNodes[0];

    return f1.is.in('uninitialize', viewEl) && f1.is.function(viewEl.uninitialize)
      ? viewEl.uninitialize().then(() => this)
      : f1.done(this);
  }

  stateChange() {
    const viewEl: IAiView = this.region.childNodes[0] as IAiView;
    const locationEl = globalThis['HIP'].browser.queryOne('location', viewEl.ns);

    // no parameters will push the parameters from navigation.location in to ai-location
    //
    // locationEl.invoke(f1.read(this.lens.navigationParameters, {}, navigation));
    if (locationEl) {
      locationEl.invoke();
    }

    return f1.done(this);
  }
}
